/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm
import React from 'react';
import type { ComponentChild } from 'preact';

// Core
import { Router } from '@app';
import { Button, Popover, Link } from '@client/components';
import useContext from '@/client/context'; 
import useHeader from '@client/pages/useHeader';
import SubscribeCTA from '@/client/components/headhunter/SubscribeCTA';
import { Forbidden, UpgradeRequired } from '@common/errors';

// Types
import type { TPlans } from '@/server/services/Payment';

/*----------------------------------
- FUNCTIONS
----------------------------------*/

export function createRedirParams( askedPage?: string, reason?: string ) {

    // Create url params
    const params = new URLSearchParams();

    // Upgrade reason
    if (reason)
        params.set('reason', reason);

    if (askedPage && askedPage !== '/pricing')
        // Prevent "invalid url" error
        try {
            const urlObj = new URL(askedPage);
            if (urlObj.pathname !== '/pricing')
                params.set('redir', urlObj.pathname + urlObj.search + urlObj.hash);
        } catch (error) {}

    // Return url params (only if not empty)
    const redirParams = params.toString();
    return redirParams ? '?' + redirParams : '';
}

const upgradeUrl = (plan: string, currentUrl: string) => {
    return "/user/upgrade/" + plan + createRedirParams(currentUrl);
}

const featuresDisplay = {
    bizdev: {
        name: "Commissions",
        height: 14,
        features: {
            bizdevCommission: {
                name: 'Commission',
                display: v => <>{/*<div class="badge bg light1 s mgb-05">Business Development</div>*/}
                    <div>Up to <strong class="fg accent">{v}%</strong> on submitted missions</div></>,
                hint: v => 'You will earn a ' + v + '% commission on every mission you bring that succeed.',
                icon: /* @icon */'sack-dollar',
            },
            csmCommission: {
                name: "Bounty",
                display: v => <>{/*<div class="badge bg light2 s mgb-05">CSM</div>*/}
                    <div>Up to <strong class="fg accent">{v}%</strong> on managed missions</div></>,
                hint: () => "",
                icon: /* @icon */ "sack-dollar",
            },
            recruiterCommission: {
                name: "Bounty",
                display: v => <>{/*<div class="badge bg light3 s mgb-05">Recruiters</div>*/}
                    <div>Up to <strong class="fg accent">{v}%</strong> on accepted candidates</div></>,
                hint: () => "",
                icon: /* @icon */ "sack-dollar",
            }
        },
    },
    sales: {
        name: "Client Acquisition",
        height: 7,
        features: {
            jobAlerts: {
                name: "Job Alerts",
                display: v => (v === 0 ? 'No' : v) + ' job alerts',
                hint: v =>  `You can monitor up to ${v} companies and get notified when they post a new job.`,
                icon: /* @icon */ "bell",
            }
        }
    },
    missions: {
        name: "Missions",
        height: 21,
        features: {
            missionLevels: {
                name: "Access to missions",
                // Note: When the value is a JSX component (e.g. badges), the display
                // function can be adapted to render those elements appropriately.
                display: v => `Access ${v.join(' + ')} missions`,
                hint: () => "Your access level is shown by the mission badges available to you.",
                icon: /* @icon */ "bullseye",
            },
            parallelMissions: {
                name: "Simultaneous Missions",
                display: v => v === null 
                    ? <>Work on <strong class="fg accent">Unlimited</strong> simultaneous missions</> 
                    : <>Work on up to <strong class="fg accent">{v}</strong> simultaneous missions</>,
                hint: v => <>You are allowed to work on <strong>{v}</strong> missions at once.</>,
                icon: /* @icon */ "project-diagram",
            },
            candidatesToPropose: {
                name: "Candidates Submitted per Month",
                display: v => v === null 
                    ? `Unlimited candidates submission`
                    : `Submit up to ${v} candidates / month`,
                hint: v => `You can submit ${v} candidates to the platform each month.`,
                icon: /* @icon */ "user-friends",
            },
            displayedCandidates: {
                name: "Visible Candidates",
                display: v => v === null 
                    ? `Unlimited talents displayed in the ATS`
                    : `View up to ${v} of the talents you submitted`,
                hint: v => `${v} candidates will be visible in your interface.`,
                icon: /* @icon */ "eye",
            },
            missionsExclusivity: {
                name: "Mission Exclusivity",
                display: v => "Exclusivity on missions",
                hint: v => `Exclusivity on the mission is ${v ? "enabled" : "not enabled"}.`,
                icon: /* @icon */ "lock",
            },
        },
    },

    support: {
        name: "Support",
        height: 22,
        features: {
            supportDiscordCommunity: {
                name: "Public Discord Community",
                display: v => 'Public Discord Community',
                hint: v =>  `Access to the public Discord community is ${v ? "granted" : "not available"}.`,
                icon: /* @icon */ "comments",
            },
            supportBdCalls: {
                name: "Weekly BD Calls",
                display: v => 'Weekly Business Development Calls',
                hint: v => `Weekly collective calls for sales representatives (BD) are ${v ? "available" : "not available"}.`,
                icon: /* @icon */ "phone",
            },
            supportCsmCalls: {
                name: "Weekly CSM Calls",
                display: v => 'Weekly CSM Calls',
                hint: v =>
                    `Weekly collective calls for CSMs are ${typeof v === "string" ? v : v ? "available" : "not available"
                    }.`,
                icon: /* @icon */ "phone",
            },
            supportRecruiterCalls: {
                name: "Weekly Recruiter Calls",
                display: v => 'Weekly Recruiters Calls',
                hint: v => `Weekly collective calls for recruiters are ${v ? "available" : "not available"}.`,
                icon: /* @icon */ "phone",
            },
            supportPrivateDiscord: {
                name: "Private Discord Channel",
                display: v => 'Access to Private Discord Channel',
                hint: v =>
                    `A private channel on Discord is ${v ? "provided" : "not provided"}.`,
                icon: /* @icon */ "comments",
            },
            supportCeoCalls: {
                name: "CEO Calls",
                display: v => 'Monthly 30 min call with our CEO',
                hint: v => `An exclusive monthly 30-minute meeting with the CEO is ${v ? "available" : "not available"}.`,
                icon: /* @icon */ "phone",
            },
        },
    },
    training: {
        name: "Training",
        height: 12,
        features: {
            trainingElanDiscount: {
                name: 'Élan Training Discount',
                display: v => `${v === false ? "No" : v + '%'} discount on the Elan Pack`,
                hint: v =>
                    `Reduction on the price of the "Élan" training pack is ${v === false ? "not available" : typeof v === "number" ? `${v}% discount` : v
                    }.`,
                icon: /* @icon */ "chalkboard-teacher",
            },
            trainingAscensionDiscount: {
                name: 'Ascension Training Discount',
                display: v => `${v === false ? "No" : v + '%'} discount on the Ascension Pack`,
                hint: v =>
                    `Reduction on the price of the "Ascension" training pack is ${v === false ? "not available" : `${v}% discount`
                    }.`,
                icon: /* @icon */ "chalkboard-teacher",
            },
        },
    },
} as const;

const PlanCTA = ({ plan, user }: { 
    plan, 
    user 
}) => {
    return <>
        {plan.level == user.plan.level ? (
            <Button disabled class="bg info light">
                Current Plan
            </Button>
        ) : plan.level > user.plan.level ? (
            <div class="col sp-05">
                <SubscribeCTA type="primary"
                    action="now" plan={plan.id}>
                    Upgrade Now
                </SubscribeCTA>
                 <SubscribeCTA
                    action="demo" plan={plan.id}>
                    Get a Demo
                </SubscribeCTA>
            </div>
        ) : (
            null
        )}
    </>
}

/*----------------------------------
- TYPES
----------------------------------*/
export default ({ plans, context }: {
    plans: any,
    context?: UpgradeRequired
}) => {

    const { api, toast, modal, user } = useContext();

    return <>
        <header class="col w-a-6 pdv-3">
            <h1 class="xxl">
                {context?.message || <>
                    Accelerate your Recruitments with <strong class="fg accent">Super Powers ⚡️</strong>.
                </>}
            </h1>
            <p class="reading al-left">
                Join the largest incubator of recruiters in the World. Boost your client base, productivity and revenues by empowering yourself with our continuous flow of missions, advanced tools, partnership program, trainings and community.
            </p>
        </header>

        <div class="card pd-0 sep-1 row al-fill wrap">
            {Object.values(plans).map((plan) => {
                return <>

                    <div class="col sep-1 w-3 txt-left">

                        <header class="col pd-1" style={{ height: '17em' }}>
                            <div class="col al-left">
                                <strong class={"l"}>
                                    {plan.name}
                                </strong>

                                <div class="row sp-05 al-bottom">
                                    <strong style={{
                                        fontSize: '2.4rem',
                                        lineHeight: '2.4rem',
                                    }}>{plan.price}$</strong>
                                    <div class="fg silver col sp-0">
                                        per month
                                        <div>{plan.engagement}</div>
                                    </div>
                                </div>
                            </div>

                            <PlanCTA plan={plan} user={user} />
                        </header>

                        {Object.values(featuresDisplay).map((featuresGroup) => <>

                            <div class="col pd-1" style={{
                                height: featuresGroup.height + 'em'
                            }}>
                        
                                <strong>{featuresGroup.name}</strong>

                                <ul class="col">
                                    {Object.entries(featuresGroup.features).map(([ featureId, feature ]) => {
                                        const value = plan.features[ featureId ];
                                        console.log(featureId, context?.feature);
                                        return (
                                            <li class={"row" + (
                                                featureId === context?.feature ? ' card pd-05 bg info' : 
                                                value === false ? ' fg silver' : ''
                                            )}>

                                                <i src={value === false ? /* @icon */'times' : feature.icon} />

                                                <span class="col-1" {...feature.hint ? {
                                                    style: {
                                                        cursor: 'pointer'
                                                    },
                                                    onClick: () => {
                                                        modal.show(
                                                            feature.display( value ),
                                                            feature.hint( value ),
                                                        )
                                                    }
                                                } : {}}>
                                                    {feature.display( value )}
                                                </span>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>

                        </>)}

                        <footer class="pd-1">
                            <PlanCTA plan={plan} user={user} />
                        </footer>
                    </div>
                </>
            })}
        </div>
    </>
}